/**
 * A filter that takes several choices, with a search box.
 *
 * Built for the attendance screen (Parinaam, 9 Oct 2026), where Program,
 * Activity and Session each became "one or many, found by typing". A native
 * `<select multiple>` was the obvious alternative and is not usable for this:
 * it has no search, it needs ctrl-click to add a second choice, and it either
 * takes six rows of vertical space or scrolls three options at a time.
 *
 * So: a control that says what is chosen, and a panel with a search box and a
 * checkbox per option. Checkboxes rather than a full ARIA combobox because the
 * behaviour is already familiar and already announced correctly — the
 * keyboard-interaction contract of a listbox is a great deal to get right, and
 * getting it half right is worse than not claiming the role.
 *
 * **Empty means everything.** No selection is not "show me nothing"; it is "do
 * not filter on this". The callers rely on that and so does `uuidList` on the
 * API side, which turns an empty list back into an absent filter.
 *
 * Rebuilt on 9 Oct 2026 after Parinaam saw the first version: what is chosen
 * now sits at the top of the panel under its own heading, the control carries a
 * clear button of its own, and an option is a full-width row whose text wraps
 * rather than a checkbox with the name squeezed into the column beside it.
 */

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { SelectOption } from './Field';
import styles from './MultiSelectInput.module.css';
import fieldStyles from './Field.module.css';

export interface MultiSelectInputProps {
  label: string;
  options: SelectOption[];
  /** The chosen values. Controlled — the page owns the array. */
  value: string[];
  onChange: (value: string[]) => void;
  /** What the control says when nothing is chosen, e.g. "All programs". */
  placeholder?: string;
  error?: string;
  disabled?: boolean;
}

export function MultiSelectInput({
  label,
  options,
  value,
  onChange,
  placeholder = 'Any',
  error,
  disabled = false,
}: MultiSelectInputProps) {
  const controlId = useId();
  const errorId = `${controlId}-error`;
  const panelId = `${controlId}-panel`;
  const labelId = `${controlId}-label`;
  const summaryId = `${controlId}-summary`;

  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');

  /*
   * Which options were already chosen when the panel opened.
   *
   * The grouping below is computed from THIS rather than from the live
   * selection, and that is deliberate. Partitioning live would mean every
   * option jumps to the top of the list the moment it is ticked — the row under
   * the cursor moves, and the next click lands on whatever slid into its place.
   * Freezing it until the panel is reopened keeps the list still while somebody
   * works down it, and the tidy "chosen first" order is what they meet next
   * time they open it.
   */
  const [pinned, setPinned] = useState<Set<string>>(() => new Set(value));

  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const chosen = useMemo(() => new Set(value), [value]);

  const visible = useMemo(() => {
    const needle = term.trim().toLowerCase();
    if (needle === '') return options;
    return options.filter((option) => option.label.toLowerCase().includes(needle));
  }, [options, term]);

  // Searching collapses the two groups back into one list: a search result is
  // already a short list, and splitting four matches under two headings is more
  // furniture than help.
  const searching = term.trim() !== '';
  const selectedGroup = searching ? [] : visible.filter((option) => pinned.has(option.value));
  const restGroup = searching ? visible : visible.filter((option) => !pinned.has(option.value));

  /*
   * What the closed control says.
   *
   * One choice shows its own name — that is the common case and a person
   * filtering by one programme should see which. Beyond one there is no room
   * for the names, and a count is the honest summary; the panel has the detail.
   */
  const summary =
    value.length === 0
      ? placeholder
      : value.length === 1
        ? (options.find((option) => option.value === value[0])?.label ?? '1 selected')
        : `${value.length} selected`;

  // The point of the control is that you find things by typing, so the cursor
  // starts in the search box rather than needing a second click. Closing clears
  // the term, so reopening never lands on somebody's half-typed search.
  useEffect(() => {
    if (open) searchRef.current?.focus();
    else setTerm('');
  }, [open]);

  /*
   * Close when the click lands anywhere else.
   *
   * `mousedown` rather than `click`: a click that starts inside the panel and
   * finishes outside it (selecting the text of an option, say) fires `click` on
   * an ancestor, and closing the panel out from under a drag is maddening.
   */
  useEffect(() => {
    if (!open) return undefined;

    const onDocumentDown = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    };

    document.addEventListener('mousedown', onDocumentDown);
    return () => document.removeEventListener('mousedown', onDocumentDown);
  }, [open]);

  /*
   * Opening is where the grouping is fixed — see `pinned` above. Doing it here
   * rather than in an effect watching `open` is what keeps that effect honest
   * about its dependencies: it reads `value` once, at the one moment the
   * reading means anything.
   */
  const openPanel = () => {
    setPinned(new Set(value));
    setOpen(true);
  };

  const toggle = (optionValue: string) => {
    onChange(
      chosen.has(optionValue)
        ? value.filter((existing) => existing !== optionValue)
        : [...value, optionValue]
    );
  };

  const renderOption = (option: SelectOption) => (
    <li key={option.value}>
      <label className={styles.option}>
        <input
          type="checkbox"
          checked={chosen.has(option.value)}
          onChange={() => toggle(option.value)}
        />
        <span className={styles.optionLabel}>{option.label}</span>
      </label>
    </li>
  );

  return (
    <div className={fieldStyles.field}>
      <label className={fieldStyles.label} htmlFor={controlId} id={labelId}>
        {label}
      </label>

      <div
        className={styles.wrap}
        ref={wrapRef}
        // Escape closes and hands focus back, so the keyboard is never left
        // inside a panel that is no longer there.
        onKeyDown={(event) => {
          if (event.key === 'Escape' && open) {
            event.stopPropagation();
            setOpen(false);
            triggerRef.current?.focus();
          }
        }}
      >
        {/*
          The border lives on this shell rather than on the button, because the
          clear button sits inside the same outline and a button cannot contain
          another button. `:focus-within` moves the focus ring onto the shell so
          the whole control lights up, whichever half has focus.
        */}
        <div
          className={[
            styles.shell,
            error ? styles.shellError : '',
            disabled ? styles.shellDisabled : '',
            open ? styles.shellOpen : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <button
            type="button"
            id={controlId}
            ref={triggerRef}
            className={styles.trigger}
            disabled={disabled}
            aria-expanded={open}
            aria-controls={open ? panelId : undefined}
            aria-describedby={error ? errorId : undefined}
            /*
              The label AND the current value, which is what a native select
              announces. A `<label for>` on a button replaces the button's own
              text rather than adding to it, so without this the control said
              "Program" whether nothing was chosen or three things were.
            */
            aria-labelledby={`${labelId} ${summaryId}`}
            onClick={() => (open ? setOpen(false) : openPanel())}
          >
            <span
              id={summaryId}
              className={value.length === 0 ? styles.summaryEmpty : styles.summary}
            >
              {summary}
            </span>
          </button>

          {/*
            Clearing without opening the panel. Three ticked boxes used to mean
            opening the panel and unticking them one at a time, and this is the
            filter people reset most often.
          */}
          {value.length > 0 && !disabled && (
            <button
              type="button"
              className={styles.clear}
              aria-label={`Clear ${label.toLowerCase()}`}
              onClick={() => onChange([])}
            >
              <span aria-hidden="true">×</span>
            </button>
          )}

          <svg
            className={`${styles.caret} ${open ? styles.caretOpen : ''}`}
            viewBox="0 0 10 6"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M1 1l4 4 4-4"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        {open && (
          <div className={styles.panel} id={panelId}>
            <input
              ref={searchRef}
              type="search"
              className={styles.search}
              placeholder="Search…"
              aria-label={`Search ${label.toLowerCase()}`}
              value={term}
              onChange={(event) => setTerm(event.target.value)}
            />

            {visible.length === 0 ? (
              <p className={styles.none}>Nothing matches that.</p>
            ) : (
              <div className={styles.scroller}>
                {selectedGroup.length > 0 && (
                  <>
                    <p className={styles.groupHeading}>Selected</p>
                    <ul className={styles.list}>{selectedGroup.map(renderOption)}</ul>
                    {restGroup.length > 0 && <hr className={styles.divider} />}
                  </>
                )}

                {restGroup.length > 0 && (
                  <ul className={styles.list}>{restGroup.map(renderOption)}</ul>
                )}
              </div>
            )}

            {value.length > 0 && (
              <button
                type="button"
                className={styles.clearAll}
                onClick={() => onChange([])}
              >
                Clear selection
              </button>
            )}
          </div>
        )}
      </div>

      {error && (
        <p className={fieldStyles.error} id={errorId}>
          {error}
        </p>
      )}
    </div>
  );
}
